/**
 * The tab a finger is on, before the router has moved (`D193`).
 *
 * The active pill is derived from the URL, and the router commits a
 * navigation inside a transition: the pill moved only once the new screen
 * had rendered, a beat after the tap. A native tab bar answers the touch
 * itself. So the press writes the destination here and the bar draws it
 * active at once; the URL catching up clears it, and so does a cancelled
 * press or a navigation that never lands.
 */
import { useSyncExternalStore } from "react";
import type { Screen } from "../../app/search";

/** A press that has not landed by then never will (a no-op, an error). */
const PENDING_TIMEOUT_MS = 1500;

let pending: Screen | null = null;
let timer: ReturnType<typeof setTimeout> | undefined;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

export function setPendingTab(next: Screen | null): void {
  clearTimeout(timer);
  if (next !== null) {
    timer = setTimeout(() => setPendingTab(null), PENDING_TIMEOUT_MS);
  }
  if (pending === next) {
    return;
  }
  pending = next;
  emit();
}

export function pendingTab(): Screen | null {
  return pending;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function usePendingTab(): Screen | null {
  return useSyncExternalStore(subscribe, pendingTab, () => null);
}

/** The tab to draw active: the pressed one while it is landing, else the URL's. */
export function activeTab(screen: Screen, pressed: Screen | null): Screen {
  return pressed ?? screen;
}
